<template>
  <section>
    <v-card class="items-card">
      <v-list v-if="initialized">
        <v-list-group
          v-for="(product, index) in productList"
          :key="index"
          append-icon=""
        >
          <template v-slot:activator>
            <v-list-item-avatar :width="imgWidth" :height="imgHeight" rounded>
              <v-img
                v-if="
                  product.Product.PictureUrl != null &&
                  product.Product.PictureUrl !== ''
                "
                :src="product.Product.PictureUrl"
              />
              <v-icon v-else>mdi-image-off</v-icon>
              <!-- Highlighting label -->
              <HighlightingLabel
                v-if="product.Item"
                :label="product.Item.HighlightingLabel"
                :visible="product.Item.Highlighted"
              />
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title v-text="product.Product.Name" class="mb-2">
              </v-list-item-title>
              <v-list-item-subtitle class="text--primary font-weight-medium">
                {{ formatter.format(getProductPrice(product)) }}
                <span v-if="isProfUser" class="text-caption text--secondary">
                  {{ $translate('EXCL_VAT') }}
                </span>
              </v-list-item-subtitle>
              <v-list-item-subtitle>
                {{ $translate('QUANTITY') }}:
                <span class="text--primary font-weight-medium">
                  {{ getDisplayQuantity(product.Product, product.Quantity) }}
                </span>
                <span v-if="isWeightedProduct(product)">
                  {{ getDisplayUnit(product.Product) }}
                </span>
              </v-list-item-subtitle>
              <v-list-item-subtitle>{{ product.ItemDef }}</v-list-item-subtitle>
              <v-list-item-subtitle v-if="product.OptionSelectionSummary">
                {{ product.OptionSelectionSummary }}
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action>
              <v-btn icon>
                <v-icon color="primary">mdi-pencil</v-icon>
              </v-btn>
            </v-list-item-action>
          </template>

          <v-list-item
            style="background-color: #eeeeee"
            :key="`${index}-expansion`"
          >
            <v-col>
              <!-- Quantity operations -->
              <v-row class="ma-0 mb-2" align="center">
                <div>{{ $translate('AMOUNT_OF_PRODUCTS') }}:</div>

                <v-spacer />

                <v-btn-toggle rounded>
                  <v-btn
                    small
                    :disabled="decreaseBtnDisabled(product)"
                    @click="decreaseQuantity(product)"
                  >
                    <v-icon small>ti-minus</v-icon>
                  </v-btn>
                  <v-card
                    height="28"
                    min-width="60"
                    flat
                    outlined
                    tile
                    class="text-center px-3"
                  >
                    {{ getDisplayQuantity(product.Product, product.Quantity) }}
                    <span v-if="isWeightedProduct(product)">
                      {{ getDisplayUnit(product.Product) }}
                    </span>
                  </v-card>
                  <v-btn
                    small
                    :disabled="increaseBtnDisabled(product)"
                    @click="increaseQuantity(product)"
                  >
                    <v-icon small>ti-plus</v-icon>
                  </v-btn>
                </v-btn-toggle>
              </v-row>

              <v-row class="ma-0" justify="end">
                <v-btn
                  color="primary"
                  depressed
                  class="text-capitalize primary-font-color"
                  @click="onRemoveBtnClick(index)"
                >
                  {{ $translate('REMOVE') }}
                </v-btn>
              </v-row>
            </v-col>
          </v-list-item>

          <v-divider v-if="index < productList.length - 1" :key="index" />
        </v-list-group>
      </v-list>
    </v-card>
  </section>
</template>

<script>
import {
  getTotalPriceInclVAT,
  getTotalPriceExclVAT,
  personalDiscount,
  applyPersonalDiscount,
  quantityDecreasable,
  quantityIncreasable,
  getDisplayUnit,
  getDisplayQuantity,
} from '@/objects/Product';
import utility from '@/shared/utility';
import HighlightingLabel from '@/components/generalComponents/HighlightingLabel';

export default {
  name: 'SubscriptionProductsSelection',
  components: {
    HighlightingLabel,
  },
  props: {
    products: Array,
  },
  data() {
    return {
      productList: [],
      initialized: false,
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),
    };
  },
  computed: {
    imgWidth() {
      const breakPontObj = this.$vuetify.breakpoint;

      switch (breakPontObj.name) {
        case 'xs':
          return 80;
        case 'sm':
          return 100;
        case 'md':
          return 130;
        case 'lg':
        case 'xl':
        default:
          return 150;
      }
    },
    imgHeight() {
      return this.imgWidth * 0.618;
    },
    isProfUser() {
      return this.$store.getters.isProfUser;
    },
  },
  watch: {
    products() {
      this.initProducts();
    },
  },
  mounted() {
    this.initProducts();
    this.initialized = true;
  },
  methods: {
    initProducts() {
      this.productList = [];

      if (this.products != null) {
        this.productList = [...this.products];
      }
    },
    isWeightedProduct(product) {
      if (
        product != null &&
        product.Product != null &&
        product.Product.Type === 'Weighted'
      ) {
        return true;
      }

      return false;
    },
    showTotalPrice(product) {
      // Weighted product with fixed price
      if (
        this.isWeightedProduct(product) &&
        product.Product.WeightedInfo.FixedPrice
      ) {
        return true;
      }

      if (product.Quantity != null && product.Quantity > 0) {
        return true;
      }

      return false;
    },
    increaseBtnDisabled(product) {
      return !quantityIncreasable(
        product.Product,
        product.Item,
        product.Quantity
      );
    },
    decreaseBtnDisabled(product) {
      return !quantityDecreasable(
        product.Product,
        product.Item,
        product.Quantity
      );
    },
    increaseQuantity(product) {
      if (this.isWeightedProduct(product)) {
        // eslint-disable-next-line no-param-reassign
        product.Quantity += product.Product.WeightedInfo.StepSize;
      } else {
        // eslint-disable-next-line no-param-reassign
        product.Quantity += 1;
      }

      this.updateTotalPrice(product);
      this.onSelectionChange();
    },
    decreaseQuantity(product) {
      if (this.isWeightedProduct(product)) {
        // eslint-disable-next-line no-param-reassign
        product.Quantity -= product.Product.WeightedInfo.StepSize;
      } else {
        // eslint-disable-next-line no-param-reassign
        product.Quantity -= 1;
      }

      this.updateTotalPrice(product);
      this.onSelectionChange();
    },
    updateTotalPrice(product) {
      // eslint-disable-next-line no-param-reassign
      product.Price = this.getProductPriceInclVAT(product);
    },
    onRemoveBtnClick(index) {
      this.productList.splice(index, 1);
      this.onSelectionChange();
    },
    onSelectionChange() {
      this.$emit('change', this.productList);
    },
    getProductPrice(product) {
      if (applyPersonalDiscount(product)) {
        return this.getProductPriceWithPersonalDiscount(product);
      }
      return this.getProductPriceWithoutPersonalDiscount(product);
    },
    getProductPriceInclVAT(product) {
      if (applyPersonalDiscount(product)) {
        const priceInclVat = getTotalPriceInclVAT(product);
        return utility.getDiscountPrice(
          priceInclVat,
          personalDiscount(product)
        );
      }
      return getTotalPriceInclVAT(product);
    },
    getProductPriceWithPersonalDiscount(product) {
      let price = this.getProductPriceWithoutPersonalDiscount(product);
      if (price != null && applyPersonalDiscount(product)) {
        price = utility.getDiscountPrice(price, personalDiscount(product));
      }
      return price;
    },
    getProductPriceWithoutPersonalDiscount(product) {
      if (this.isProfUser) {
        return getTotalPriceExclVAT(product);
      }
      return getTotalPriceInclVAT(product);
    },
    getDisplayUnit,
    getDisplayQuantity,
  },
};
</script>

<style scoped lang="scss">
.items-card {
  width: 100%;
  min-height: 30em;
  max-height: 30em;
  overflow-x: auto;
  overflow-y: auto;
}
</style>
